import { computed, type Ref } from "vue";
import { useQueries, useQuery } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";
import type { ConnectionTemplateListItem, ConnectionTemplateDetail } from "@/types";

const BASE = `${import.meta.env.VITE_API_URL}/connection-templates`;

// READ — the published template catalogue (the "New Connection" picker source).
export function useConnectionTemplates() {
  const { data, isFetching: templatesLoading, error: templatesError, refetch: refetchTemplates } = useQuery({
    queryKey: ["connection-templates"],
    queryFn: () => fetchWrapper.get<ConnectionTemplateListItem[]>(BASE),
  });
  const templates = computed(() => data.value ?? []);
  return { templates, templatesLoading, templatesError: templatesError as Ref<ApiError | null>, refetchTemplates };
}

// READ — several templates' details at once (the builder's scope tree needs the field schema of
// every slot-referenced template). Shares the per-template cache key with useConnectionTemplate.
export function useConnectionTemplateDetails(ids: Ref<string[]>) {
  const results = useQueries({
    queries: computed(() =>
      ids.value.map((id) => ({
        queryKey: ["connection-template", id],
        queryFn: () => fetchWrapper.get<ConnectionTemplateDetail>(`${BASE}/${id}`),
      })),
    ),
  });
  const templatesById = computed<Record<string, ConnectionTemplateDetail>>(() =>
    Object.fromEntries(results.value.flatMap((r) => (r.data ? [[r.data.id, r.data]] : []))),
  );
  return { templatesById };
}

// READ — a single template's per-method field schema (drives the setup form).
export function useConnectionTemplate(id: Ref<string>) {
  const { data: template, isFetching: templateLoading, error: templateError } = useQuery({
    queryKey: computed(() => ["connection-template", id.value]),
    queryFn: () => fetchWrapper.get<ConnectionTemplateDetail>(`${BASE}/${id.value}`),
    enabled: computed(() => !!id.value),
  });
  return { template, templateLoading, templateError: templateError as Ref<ApiError | null> };
}
